import { type ReactNode, useState } from "react";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { Sheet } from "../../shared/phone/Sheet";
import { PnRow, Segmented } from "../../shared/phone/Row";
import type {
  PartnerPayoutDoc,
  PartnerPayoutHistoryEntry,
  PartnerPayoutReward,
  PartnerPayoutsView,
  PartnerProgramMembership,
} from "../../shared/types";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { longDate, money, money2, periodLabel, shortDate } from "../lib/format";
import { type PayKind, usePayoutMethodForm } from "../components/PayoutMethodModal";
import { Icon } from "../components/phone/Icon";
import { EmptyBlock, Monogram, PoweredBy } from "../components/phone/parts";
import { toast } from "../components/phone/Toast";

/** How many rewards on the way show before "Show all". */
const FIRST_FEW = 3;

const SEG_LABEL: Record<PayKind, string> = { paypal: "PayPal", wise: "Wise", bank: "Bank", other: "Other" };

function rate(value: number, unit: PartnerPayoutReward["unit"], currency: string): string {
  if (unit === "percent") {
    const pct = value / 100;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  }
  return money(value, currency);
}

function basisLine(reward: PartnerPayoutReward): string {
  if (reward.duration === "recurring") return "of every payment while they stay";
  if (reward.duration === "forever") return "per sale, for good";
  return "per sale, one time";
}

/** A document's line in Getting paid: what it is and whether it is done. */
function docRow(doc: PartnerPayoutDoc): { title: string; sub: string } {
  if (doc.kind === "agreement") return { title: doc.satisfied ? "Agreement signed" : "Agreement not signed", sub: "Partner agreement" };
  if (doc.kind === "tax") return { title: doc.satisfied ? `${doc.detail ?? "Tax form"} on file` : "No tax form yet", sub: "Tax form" };
  return { title: doc.satisfied ? "ID verified" : "ID not verified", sub: doc.mandatory ? "Needed before payout" : "Optional" };
}

function methodDetail(e: PartnerPayoutHistoryEntry): string | null {
  return e.methodSnapshot?.email ?? e.methodSnapshot?.account ?? null;
}

function kv(k: string, v: ReactNode) {
  return (
    <div className="pn-row static po-kv">
      <span className="m">
        <span className="t">{k}</span>
      </span>
      <span className="v">{v}</span>
    </div>
  );
}

function field(id: string, label: string, input: ReactNode) {
  return (
    <div className="pn-field">
      <label htmlFor={id}>{label}</label>
      {input}
    </div>
  );
}

/** One payout the owner recorded, in full. */
function PaidSheet({ entry, productName, currency, onClose }: { entry: PartnerPayoutHistoryEntry; productName: string; currency: string; onClose: () => void }) {
  const period = periodLabel(entry.periodStart, entry.periodEnd);
  const sentTo = methodDetail(entry);
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Payout, ${shortDate(entry.at)}`}
      right={
        <button type="button" className="pn-tbtn strong" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="po-paid">
        <span className="po-big">{money2(entry.amountCents, currency)}</span>
        <span className="pn-pill good">
          <i />
          Paid
        </span>
      </div>
      <div className="pn-grp">
        {kv("Date paid", longDate(entry.at) || "Recorded")}
        {kv("Method", entry.methodLabel ?? "Recorded by the owner")}
        {sentTo ? kv("Sent to", sentTo) : null}
        {entry.reference ? kv("Reference", entry.reference) : null}
        {kv("Period", period ?? "Not set")}
        {kv("Program", productName)}
      </div>
      {entry.note ? (
        <>
          <div className="pn-cap po-cap2">Note from {productName}</div>
          <div className="pn-card po-notecard">{entry.note}</div>
        </>
      ) : null}
      <p className="pn-note">
        {period ? `Your cleared rewards for ${period}.` : "Your cleared rewards for this run."} {productName} paid you directly and recorded it
        here. Flashadz never holds your money.
      </p>
    </Sheet>
  );
}

/** How the partner wants to be paid, as a sheet that scrolls and keeps Save above the keyboard. */
function MethodSheet({
  programId,
  view,
  onClose,
  onSaved,
}: {
  programId: string;
  view: PartnerPayoutsView;
  onClose: () => void;
  onSaved: (membership: PartnerProgramMembership) => void;
}) {
  const f = usePayoutMethodForm(programId, view.payoutMethod, onSaved);
  return (
    <Sheet
      open
      onClose={onClose}
      title="Payout method"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      foot={
        <button type="button" className="pn-btn pri wide" disabled={!f.canSave()} onClick={() => void f.save()}>
          {f.busy ? "Saving…" : "Save"}
        </button>
      }
    >
      <form
        className="po-pm"
        onSubmit={(e) => {
          e.preventDefault();
          void f.save();
        }}
      >
        <Segmented
          label="How you want to be paid"
          value={f.kind}
          options={(["paypal", "wise", "bank", "other"] as PayKind[]).map((k) => ({ value: k, label: SEG_LABEL[k] }))}
          onChange={f.setKind}
        />
        <div className="pn-form">
          {f.kind === "paypal" || f.kind === "wise"
            ? field(
                "po-pm-email",
                f.kind === "paypal" ? "PayPal email" : "Wise email or Wisetag",
                <input
                  id="po-pm-email"
                  className="pn-inp"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={f.email}
                  onChange={(e) => f.setEmail(e.target.value)}
                  placeholder="name@email.com"
                />,
              )
            : null}
          {f.kind === "bank" ? (
            <>
              {field(
                "po-pm-holder",
                "Account holder name",
                <input id="po-pm-holder" className="pn-inp" autoComplete="name" autoCapitalize="words" value={f.holder} onChange={(e) => f.setHolder(e.target.value)} placeholder="As it appears on the account" />,
              )}
              {field(
                "po-pm-bank",
                "Bank name",
                <input id="po-pm-bank" className="pn-inp" autoComplete="off" autoCapitalize="words" value={f.bank} onChange={(e) => f.setBank(e.target.value)} placeholder="e.g. Chase" />,
              )}
              {field(
                "po-pm-account",
                "Account number or IBAN",
                <input id="po-pm-account" className="pn-inp" autoComplete="off" autoCapitalize="characters" spellCheck={false} value={f.account} onChange={(e) => f.setAccount(e.target.value)} />,
              )}
              {field(
                "po-pm-routing",
                "Routing or SWIFT",
                <input id="po-pm-routing" className="pn-inp" autoComplete="off" autoCapitalize="characters" spellCheck={false} value={f.routing} onChange={(e) => f.setRouting(e.target.value)} />,
              )}
            </>
          ) : null}
          {f.kind === "other" ? (
            <>
              {field(
                "po-pm-other",
                "Method name",
                <input id="po-pm-other" className="pn-inp" autoComplete="off" autoCapitalize="words" value={f.otherName} onChange={(e) => f.setOtherName(e.target.value)} placeholder="Venmo, Zelle, Revolut" />,
              )}
              {field(
                "po-pm-how",
                "How to pay you",
                <textarea id="po-pm-how" className="pn-inp" rows={3} value={f.otherHow} onChange={(e) => f.setOtherHow(e.target.value)} placeholder="Handle, link, or steps to send you money" />,
              )}
            </>
          ) : null}
          {f.error ? (
            <p className="po-err" role="alert">
              {f.error}
            </p>
          ) : null}
        </div>
        <p className="pn-note">{view.productName} pays you directly. Flashadz records how you want to be paid, it never holds your money.</p>
      </form>
    </Sheet>
  );
}

/**
 * Payouts on a phone and an iPad: the next payout first, with Ready, On hold and Paid to date, then what
 * is on the way, the payment history, how the partner earns and how they get paid. Read only: the owner
 * pays directly and Flashadz records it. Two columns on an iPad.
 */
export function PayoutsNative({
  view,
  error,
  onRetry,
  programId,
  onMethodSaved,
}: {
  view: PartnerPayoutsView | null;
  error: string | null;
  onRetry: () => void;
  programId: string;
  onMethodSaved: (membership: PartnerProgramMembership) => void;
}) {
  const { currentMembership } = usePartnerAuth();
  const [all, setAll] = useState(false);
  const [paid, setPaid] = useState<PartnerPayoutHistoryEntry | null>(null);
  const [editing, setEditing] = useState(false);

  let body: ReactNode;
  if (error && !view) {
    body = (
      <div className="pn-stack">
        <EmptyBlock icon="info" title="Something went wrong">
          {error}
        </EmptyBlock>
        <div className="pn-pad">
          <button type="button" className="pn-btn soft wide" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!view) {
    body = <div className="po-loading" role="status" aria-label="Loading" />;
  } else {
    const cur = view.currency;
    const resign = currentMembership?.needsResign ? currentMembership : null;
    const gettingPaid = (
      <section>
        <div className="pn-sh">
          <h2>Getting paid</h2>
        </div>
        <div className="pn-grp">
          {view.payoutMethod ? (
            <PnRow onClick={() => setEditing(true)} icon={<Icon name="wallet" />} title={view.payoutMethodLabel ?? "Payout method"} sub={view.payoutMethodDetail ?? undefined} chevron />
          ) : (
            <PnRow onClick={() => setEditing(true)} icon={<Icon name="wallet" />} iconTone="warn" title="Add a payout method" sub={`So ${view.productName} can pay you`} chevron />
          )}
          {resign ? (
            <PnRow to={`/onboarding/${resign.programId}`} icon={<Icon name="file" />} iconTone="warn" title="New terms to sign" sub="Partner agreement" />
          ) : null}
          {view.documents
            .filter((d) => !(resign && d.kind === "agreement"))
            .map((d) => {
              const r = docRow(d);
              return <PnRow key={d.kind} icon={<Icon name={d.satisfied ? "check" : "info"} />} iconTone={d.satisfied ? "good" : "plain"} title={r.title} sub={r.sub} />;
            })}
        </div>
        <p className="pn-note">{view.productName} pays you directly. Flashadz records what you are owed, it never holds your money.</p>
      </section>
    );

    if (!view.hasActivity) {
      body = (
        <div className="pn-stack">
          <EmptyBlock icon="coin" title="No rewards yet">
            Once your referrals become customers, your rewards show up here and move from on hold to ready to pay.
          </EmptyBlock>
          {gettingPaid}
          <PoweredBy />
        </div>
      );
    } else {
      const gated = view.position.state === "blocked" || view.position.state === "below_min";
      const heroCents = gated ? view.position.owedCents : view.nextTotalCents;
      const when = gated ? null : view.clearing.length ? `by ${shortDate(view.nextClearsAt)}` : view.readyCents > 0 ? "ready now" : null;
      const way = all ? view.clearing : view.clearing.slice(0, FIRST_FEW);
      const reward = view.reward;
      body = (
        <div className="pn-stack po-wide">
          <div className="po-duo">
            <div className="po-col">
              <section className="pn-card po-next">
                <div className="po-k">{gated ? "On hold" : "Next payout"}</div>
                <div className="po-nx">
                  <span className="po-big">{money(heroCents, cur)}</span>
                  {when ? <span>{when}</span> : null}
                </div>
                {gated && view.position.reason ? <p className="po-why">{view.position.reason}</p> : null}
                <div className="po-fig">
                  <div>
                    <span className="po-fl">
                      <i className="good" />
                      Ready
                    </span>
                    <b>{money(view.readyCents, cur)}</b>
                  </div>
                  <div>
                    <span className="po-fl">
                      <i className="warn" />
                      On hold
                    </span>
                    <b>{money(view.onHoldCents, cur)}</b>
                  </div>
                  <div>
                    <span className="po-fl">
                      <i />
                      Paid to date
                    </span>
                    <b>{money(view.paidToDateCents, cur)}</b>
                  </div>
                </div>
              </section>

              <section>
                <div className="pn-sh">
                  <h2>On the way</h2>
                  <span className="po-shn">{money(view.onHoldCents, cur)}</span>
                </div>
                {view.clearing.length ? (
                  <div className="pn-grp">
                    {way.map((c) => {
                      const name = c.personLabel ?? "New customer";
                      return (
                        <div className="pn-row static" key={c.referralId}>
                          <Monogram name={name} />
                          <span className="m">
                            <span className="t">{name}</span>
                            <span className="s">
                              Sale {shortDate(c.soldAt)}
                              {c.clearsAt ? `, clears ${shortDate(c.clearsAt)}` : ""}
                            </span>
                          </span>
                          <span className="po-r2">
                            <b>{money(c.amountCents, cur)}</b>
                            <span>{c.daysRemaining <= 0 ? "ready" : `in ${c.daysRemaining} ${c.daysRemaining === 1 ? "day" : "days"}`}</span>
                          </span>
                        </div>
                      );
                    })}
                    {view.clearing.length > FIRST_FEW ? (
                      <button type="button" className="pn-row accent po-all" onClick={() => setAll((a) => !a)}>
                        <span className="m">
                          <span className="t">{all ? "Show fewer" : `Show all ${view.clearing.length}`}</span>
                        </span>
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <p className="po-none">Nothing clearing right now. New rewards wait here for their {view.holdDays} day hold.</p>
                )}
              </section>

              <section>
                <div className="pn-sh">
                  <h2>Payment history</h2>
                </div>
                {view.history.length ? (
                  <div className="pn-grp">
                    {view.history.map((h) =>
                      h.kind === "payout" ? (
                        <PnRow
                          key={h.id}
                          onClick={() => setPaid(h)}
                          icon={<Icon name="check" />}
                          iconTone="good"
                          title={`Payout, ${shortDate(h.at)}`}
                          sub={[h.methodLabel, h.reference].filter(Boolean).join(", ") || "Recorded by the owner"}
                          value={<span className="po-amt good">{money(h.amountCents, cur)}</span>}
                          chevron
                        />
                      ) : (
                        <PnRow
                          key={h.id}
                          icon={<Icon name="undo" />}
                          iconTone="warn"
                          title={`Clawback, ${shortDate(h.at)}`}
                          sub={`${h.personLabel ?? "A customer"} refunded, reward reversed`}
                          value={<span className="po-amt">{money(h.amountCents, cur)}</span>}
                        />
                      ),
                    )}
                  </div>
                ) : (
                  <p className="po-none">No payments recorded yet.</p>
                )}
              </section>
            </div>

            <div className="po-col">
              <section>
                <div className="pn-sh">
                  <h2>How you earn</h2>
                </div>
                <div className="pn-grp">
                  {reward.type === "ladder" && reward.tiers.length ? (
                    (() => {
                      let prev = 0;
                      return reward.tiers.map((t) => {
                        const band = t.upToCount === null ? `${prev + 1}+ sales` : `Sales ${prev + 1} to ${t.upToCount}`;
                        prev = t.upToCount ?? prev;
                        return <PnRow key={t.id} title={band} value={<b className="po-tier">{rate(t.rewardCentsOrBps, reward.unit, cur)}</b>} />;
                      });
                    })()
                  ) : (
                    <div className="pn-row static wrap po-earn">
                      <span className="po-pct">{rate(reward.valueBpsOrCents, reward.unit, cur)}</span>
                      <span className="m">
                        <span className="t">{basisLine(reward)}</span>
                        <span className="s">Each reward is held {reward.holdDays} days, then it is ready to pay.</span>
                      </span>
                    </div>
                  )}
                </div>
                {reward.type === "ladder" && reward.tiers.length ? (
                  <p className="pn-note">
                    {basisLine(reward)[0].toUpperCase() + basisLine(reward).slice(1)}. Each reward is held {reward.holdDays} days, then it is ready to pay.
                  </p>
                ) : null}
              </section>
              {gettingPaid}
            </div>
          </div>
          <PoweredBy />
        </div>
      );
    }
  }

  return (
    <div className="pn-screen">
      <PhoneNav large title="Payouts" />
      {body}
      {paid && view ? <PaidSheet entry={paid} productName={view.productName} currency={view.currency} onClose={() => setPaid(null)} /> : null}
      {editing && view ? (
        <MethodSheet
          programId={programId}
          view={view}
          onClose={() => setEditing(false)}
          onSaved={(m) => {
            setEditing(false);
            toast("Payout method saved");
            onMethodSaved(m);
          }}
        />
      ) : null}
    </div>
  );
}

